import React, { FC, useCallback, useMemo, useRef, useState } from "react";
import { AutoComplete, IOption, useAsyncAutocomplete } from "@evergis/ui";
import { QueryLayerServiceConfigurationDc } from "@evergis/api";

import { TextFilterContainer } from "./styled";
import { getConfigFilter } from "../../utils/getConfigFilter";
import { useWidgetContext } from "../../../Dashboard/hooks/useWidgetContext";
import { useUpdateDataSource } from "../../../Dashboard/hooks/useUpdateDataSource";
import { useRelatedDataSourceAttributes } from "../../hooks/useRelatedDataSourceAttributes";
import { useLayerParams } from "../../../../hooks/eql/useLayerParams";
import { WidgetFilterProps } from "./types";
import { SelectedFilters } from "../../types";

export const TextFilter: FC<WidgetFilterProps> = ({ type, filter, config }) => {
  const { filters, changeFilters, dataSources } = useWidgetContext(type);
  const suggestRef = useRef<HTMLElement | null>(null);
  const [totalCount, setTotalCount] = useState(0);

  const { filters: configFilters, dataSources: configDataSources } = config;
  const { filterName, searchFilterName, placeholder, width, height, multiSelect } = filter.options;

  const { attributes, layerInfo } = useRelatedDataSourceAttributes({ config, elementConfig: filter, dataSources });
  const { eqlParameters } = (layerInfo?.configuration || {}) as QueryLayerServiceConfigurationDc;
  const [layerParams] = useLayerParams(layerInfo);
  const [value, setValue] = useState("");

  const configFilter = useMemo(() => getConfigFilter(filterName, configFilters), [configFilters, filterName]);

  const searchFilter = useMemo(
    () => getConfigFilter(searchFilterName, configFilters),
    [configFilters, searchFilterName],
  );
  const searchDataSource = useMemo(
    () =>
      !!searchFilter?.relatedDataSource &&
      configDataSources.find(({ name }) => name === searchFilter.relatedDataSource),
    [configDataSources, searchFilter?.relatedDataSource],
  );

  const updateDataSource = useUpdateDataSource({
    dataSource: searchDataSource,
    config,
    filters,
    attributes,
    layerParams,
    eqlParameters,
  });

  const [options, setOptions] = useState<IOption[]>([]);

  const onSelect = useCallback(
    (selected: IOption[]) => {
      const lastSelected = selected[selected.length - 1];

      changeFilters({
        [filterName]: {
          value: multiSelect ? selected.map(item => item.value) : selected[selected.length - 1]?.value || "",
        },
      });
      setValue(multiSelect ? "" : lastSelected?.text || "");
    },
    [changeFilters, filterName, multiSelect],
  );

  const { asyncCallback, loadNext } = useAsyncAutocomplete({
    options,
    setOptions,
    setTotal: setTotalCount,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    formatTotal: (data: Record<string, any>) => data.totalCount,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    formatOptions: (data: Record<string, any>) => {
      return data.items.map(item => ({
        text: (item.attributes.attributes || item.attributes)[searchFilter.attributeAlias],
        value: (item.attributes.attributes || item.attributes)[searchFilter.attributeValue],
      }));
    },
  });

  const search = useCallback(
    (searchValue: string, isLoadNext = false): Promise<void> => {
      if (!searchFilter?.attributeAlias || !searchFilter?.attributeValue) return;

      const newFilter: SelectedFilters = {
        [searchFilterName]: {
          value: `%${searchValue || ""}%`,
        },
      };

      asyncCallback({
        value: searchValue,
        promise: updateDataSource(newFilter, isLoadNext ? options.length : 0),
        isLoadNext,
      });
    },
    [
      searchFilter?.attributeAlias,
      searchFilter?.attributeValue,
      searchFilterName,
      asyncCallback,
      updateDataSource,
      options.length,
    ],
  );

  const infiniteScrollProps = useMemo(
    () => ({
      loadNext: () => loadNext(() => search(value, true)),
      hasMore: totalCount > options.length,
    }),
    [totalCount, options.length, loadNext, search, value],
  );

  if (!configFilter) return null;

  return (
    <TextFilterContainer>
      <AutoComplete
        value={value}
        placeholder={placeholder}
        options={options}
        zIndex={100}
        width={width ? `${width}px` : "17.25rem"}
        menuHeight={height ? `${height}px` : "10rem"}
        withInfiniteScroll
        infiniteScrollProps={infiniteScrollProps}
        multiSelect={multiSelect}
        selected={filters[filterName]?.value as string[]}
        setOptions={setOptions}
        onChange={setValue}
        onSelect={onSelect}
        onGetInnerRef={ref => (suggestRef.current = ref)}
        asyncCallback={search}
        onBlur={() => {
          setOptions([]);
        }}
      />
    </TextFilterContainer>
  );
};
